import { useEffect } from "react"

const SITE_KEY = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITEKEY
const LOAD_TIMEOUT = 10000

let loading: Promise<any> | null = null

/**
 * Loads the script once per page, shared by every form
 */
function loadRecaptcha(): Promise<any> {
    if (!loading) {
        loading = new Promise((resolve, reject) => {
            const script = document.createElement("script")
            script.src = "https://www.google.com/recaptcha/api.js?render=" + SITE_KEY
            script.async = true
            script.onload = () => {
                const grecaptcha = window['grecaptcha']
                if (!grecaptcha) {
                    reject(new Error('reCAPTCHA unavailable'))
                    return
                }
                grecaptcha.ready(() => resolve(grecaptcha))
            }
            script.onerror = () => reject(new Error('reCAPTCHA script failed to load'))
            document.head.appendChild(script)
        })
        // A failed load can be retried on the next submit
        loading.catch(() => {
            loading = null
        })
    }
    return loading
}

/**
 * The script and its badge stay in the document across client side navigations :
 * the badge is only shown while at least one form using reCAPTCHA is displayed
 */
let activeForms = 0

function updateBadge() {
    const badge = document.querySelector<HTMLElement>('.grecaptcha-badge')
    if (badge) {
        badge.style.visibility = activeForms > 0 ? 'visible' : 'hidden'
    }
}

/**
 * To call in the form component : reCAPTCHA v3 scores the visitor from what it observes on the page,
 * loading it when the form is displayed rather than on submit gives it something to observe
 * `enabled` delays the loading, e.g. until a collapsed form is opened
 */
export function useRecaptcha(enabled: boolean = true) {
    useEffect(() => {
        if (!enabled) {
            return
        }

        activeForms++
        // The badge is created by the script, it only exists once loaded
        loadRecaptcha().then(updateBadge, () => {})

        return () => {
            activeForms--
            updateBadge()
        }
    }, [enabled])
}

/**
 * Asks for a token on submit : a token expires after 2 minutes, it can't be generated in advance
 * `action` names the form in the token, the server checks it to refuse a token generated elsewhere on the site
 */
export async function executeRecaptcha(action?: string): Promise<string> {
    let timer: ReturnType<typeof setTimeout>
    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('reCAPTCHA load timeout')), LOAD_TIMEOUT)
    })

    try {
        const grecaptcha = await Promise.race([loadRecaptcha(), timeout])
        return await grecaptcha.execute(SITE_KEY, action ? { action } : undefined)
    }
    finally {
        clearTimeout(timer)
    }
}

